import { memo, useCallback, useMemo } from 'react';
import { Button, Text, useTheme } from '@Sage/hoi-poi-ui';

import { CAMPAIGNS_MARKETING } from 'constants/Entities';
import UserChip from 'containers/components/chips/UserChip';
import useEntityDetail from 'containers/components/EntityDetail/hooks/useEntityDetail';
import { logMarketingCampaignsEvent } from 'containers/campaigns/marketing/utils/marketingTracking';
import { getMomentFromDateBackend } from 'utils/dates';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';
import { ensureRoute } from 'utils/routes';
import { getMarketingCampaignRecipientsBreakdown } from '../../../../utils/marketingCampaignActivity';

import '../../styles.scss';

const DetailMarketingCampaign = memo(({ activity }) => {
    const theme = useTheme();
    const { hasTabsEnabled, openTab } = useEntityDetail();

    const campaign = activity?.Campaign;
    const campaignId = activity?.Id;
    const campaignName = campaign?.Subject;
    const recipients = useMemo(
        () => getMarketingCampaignRecipientsBreakdown(campaign?.Recipients),
        [campaign?.Recipients],
    );
    const recipientsSummary = useMemo(() => {
        if (recipients?.total === undefined || recipients?.total === null) return null;

        const recipientsBreakdown = recipients.breakdown?.map(({ text }) => text).join(', ');
        const totalText = `${recipients.total} ${getLiteral('label_recipients')}`;

        return recipientsBreakdown ? `${totalText} - ${recipientsBreakdown}` : totalText;
    }, [recipients]);
    const sentDate = useMemo(() => {
        if (!activity?.ActivityDate) return null;

        const activityDate = getMomentFromDateBackend(activity.ActivityDate, null, true);
        return getLiteralWithParameters('label_date_with_time', [
            activityDate.format('LL'),
            activityDate.format('LT'),
        ]);
    }, [activity?.ActivityDate]);

    const handleViewCampaignDetails = useCallback(() => {
        if (!campaignId) return;

        logMarketingCampaignsEvent({
            submodule: 'campaign',
            functionality: 'detailViewFromActivity',
        });

        if (hasTabsEnabled) {
            openTab({
                entity: CAMPAIGNS_MARKETING,
                id: campaignId,
                avoidRedirects: true,
            });
            return;
        }

        ensureRoute(`${CAMPAIGNS_MARKETING.route}/${campaignId}`);
    }, [campaignId, hasTabsEnabled, openTab]);

    const rows = useMemo(
        () =>
            [
                {
                    key: 'activity-type',
                    label: getLiteral('label_activity_type'),
                    value: activity?.Type,
                },
                {
                    key: 'campaign-name',
                    label: getLiteral('label_name'),
                    value: campaignName,
                },
                {
                    key: 'recipients',
                    label: getLiteral('label_recipients'),
                    value: recipientsSummary,
                },
                {
                    key: 'sent-date',
                    label: getLiteral('label_sent_date'),
                    value: sentDate,
                },
                {
                    key: 'sender',
                    label: getLiteral('label_sender'),
                    content: activity?.ActivityOwner ? (
                        <UserChip idUser={activity?.IdActivityOwner} remotePopover={true}>
                            <Text className="fm-activities-marketing-campaign__row-value fm-activities-marketing-campaign__row-value--sender">
                                {activity.ActivityOwner}
                            </Text>
                        </UserChip>
                    ) : null,
                },
            ].filter(({ value, content }) => (value != null && value !== '') || content),
        [
            activity?.ActivityOwner,
            activity?.IdActivityOwner,
            activity?.Type,
            campaignName,
            recipientsSummary,
            sentDate,
        ],
    );

    if (!campaign) return null;

    return (
        <div className="fm-activities-marketing-campaign">
            <div className="fm-activities-marketing-campaign__header">
                <Text medium>{getLiteral('label_key_data')}</Text>
            </div>
            <div className="fm-activities-marketing-campaign__rows">
                {rows.map(({ key, label, value, content }) => (
                    <div key={key} className="fm-activities-marketing-campaign__row">
                        <Text
                            color={theme.colors.utility.textSecondary}
                            className="fm-activities-marketing-campaign__row-label"
                        >
                            {label}
                        </Text>
                        {content || (
                            <Text className="fm-activities-marketing-campaign__row-value">
                                {value}
                            </Text>
                        )}
                    </div>
                ))}
            </div>

            {campaignId && (
                <div className="fm-activities-marketing-campaign__actions">
                    <Button icon="launch" onClick={handleViewCampaignDetails}>
                        {getLiteral('label_view_campaign_details')}
                    </Button>
                </div>
            )}
        </div>
    );
});

DetailMarketingCampaign.displayName = 'DetailMarketingCampaign';

export default DetailMarketingCampaign;
